import { ChevronDown } from 'lucide-react';
import React, { useState } from 'react';
import { fmtId, fmtTool } from './utils';

export function PlaybookCard({ processId }: { processId?: string }) {
  return (
    <div className="bx-proc-chip" style={{ cursor: 'default' }}>
      <span className="bx-eval-icon">📖</span>
      <span className="bx-proc-chip-label">Playbook: <strong>{fmtId(processId)}</strong></span>
    </div>
  );
}

export function TemplateCard({ processId, tools }: { processId?: string; tools?: string[] }) {
  const [open, setOpen] = useState(false);
  const count = tools?.length ?? 0;
  return (
    <div>
      <div className="bx-proc-chip" onClick={() => count > 0 && setOpen(o => !o)}>
        <span className="bx-eval-icon">📋</span>
        <span className="bx-proc-chip-label">Template: <strong>{fmtId(processId)}</strong></span>
        {count > 0 && !open && (
          <span className="bx-proc-chip-count">· {count} steps</span>
        )}
        {count > 0 && (
          <ChevronDown size={11} className={`bx-proc-chevron${open ? ' bx-proc-chevron--open' : ''}`} />
        )}
      </div>
      {open && count > 0 && (
        <ul className="bx-proc-tools">
          {tools!.map((t, idx) => (
            <li key={idx} className="bx-proc-tool-item">{t}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface ExecutorPanelProps {
  title: string;
  icon: string;
  execSteps: any[];
  isCollapsed: boolean;
  onToggle: () => void;
}

export default function ExecutorPanel({ title, icon, execSteps, isCollapsed, onToggle }: ExecutorPanelProps) {
  const [expandedTerminals, setExpandedTerminals] = useState<Set<string>>(new Set());

  return (
    <div className="bx-executor-panel">
      <button className="bx-agents-panel-header" onClick={onToggle}>
        <ChevronDown size={12} className={`bx-agents-chevron${isCollapsed ? ' bx-agents-chevron--collapsed' : ''}`} />
        <span className="bx-eval-icon" style={{ fontSize: 13 }}>{icon}</span>
        <span className="bx-agents-title">{title}</span>
        <span className="bx-proc-chip-count" style={{ marginLeft: 'auto' }}>{execSteps.length} steps</span>
      </button>
      {!isCollapsed && (
        <div className="bx-executor-steps">
          {execSteps.map((sp, si) => {
            const spd = sp.stepProgress!;
            const isOk = spd.status === 'ok';
            const avapCode = spd.output?.avap_code ?? null;
            const commitId = spd.output?.commit_id ?? null;
            const outStr = avapCode ? null : spd.output == null ? null : typeof spd.output === 'string' ? spd.output : JSON.stringify(spd.output, null, 2);
            const argsStr = !isOk && spd.args != null ? (typeof spd.args === 'string' ? spd.args : JSON.stringify(spd.args, null, 2)) : null;
            const stKey = `s${si}`;
            const isLast = si === execSteps.length - 1;
            const isExpanded = expandedTerminals.has(stKey) || isLast;

            const toggle = () => setExpandedTerminals(prev => {
              const n = new Set(prev);
              n.has(stKey) ? n.delete(stKey) : n.add(stKey);
              return n;
            });

            return (
              <div key={si} className={`bx-executor-step${isExpanded ? ' bx-executor-step--open' : ''}`}>
                <div className="bx-executor-step-header" onClick={toggle}>
                  <span className={`bx-step-dot ${isOk ? 'bx-step-dot--done' : 'bx-step-dot--error'}`} />
                  <span className="bx-tool-running-name" style={{ color: isOk ? '#4ade80' : '#f87171' }}>{fmtTool(spd.tool)}</span>
                  <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, marginLeft: 4 }}>{spd.step}/{spd.total}</span>
                  <ChevronDown size={10} style={{ marginLeft: 'auto', opacity: 0.4, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                </div>
                {isExpanded && (
                  <div className="bx-executor-step-body">
                    {outStr && <div className="bx-terminal-output bx-step-output-scroll">{outStr}</div>}
                    {avapCode && <>
                      <div className="bx-terminal-args-label">Generated AVAP code:</div>
                      <div className="bx-terminal-output bx-step-output-scroll bx-terminal-avap-code">{avapCode}</div>
                      {commitId && <div className="bx-terminal-output" style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, marginTop: 4 }}>commit: {commitId}</div>}
                    </>}
                    {argsStr && <>
                      <div className="bx-terminal-args-label">Args:</div>
                      <div className="bx-terminal-output bx-step-output-scroll" style={{ color: 'rgba(251,191,36,0.8)' }}>{argsStr}</div>
                    </>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
